import { createSlice, type PayloadAction, type Slice, type WithSlice } from '@reduxjs/toolkit';

import { rootReducer } from '@verantos-dev/app-state-base';
import {
  PROGRESS_STEP_MESSAGES,
  ResearchAssistantMessageEnumLegacy,
  type AVProgressItem,
  type ProgressState,
  type ProgressStepInfo,
  type ProgressStepType,
} from '@verantos-dev/types-common';

import { type MessageLegacy } from './messageTypes';

export enum ModelVersion {
  V1 = 'V1',
  V1_Llamaindex = 'V1_Llamaindex',
}

export const DEFAULT_MODEL_VERSION = ModelVersion.V1_Llamaindex;

export interface ResearchAssistantState {
  model: ModelVersion;
  messages: MessageLegacy[];
  isProcessing: boolean;
  isReady: boolean;
  transactionId: string | null;
  progress: ProgressState;
}

export const initialState: ResearchAssistantState = {
  model: DEFAULT_MODEL_VERSION,
  messages: [],
  isProcessing: false,
  isReady: false,
  transactionId: null,
  progress: { currentStep: null, avProgress: null, workflowId: null },
};

/**
 * Find the most recent active (incomplete) progress message in the messages array.
 * Searches backwards because other messages (e.g. RA summary) may be appended after it.
 */
const findActiveProgressMessage = (messages: MessageLegacy[]): MessageLegacy | undefined => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (
      msg.type === ResearchAssistantMessageEnumLegacy.PROGRESS &&
      msg.progressData &&
      !msg.progressData.isComplete
    ) {
      return msg;
    }
  }
  return undefined;
};

/**
 * Resolve the display message for a progress step.
 * Prefers the backend-provided custom message, falls back to the step message map.
 */
const resolveProgressMessage = (stepInfo: ProgressStepInfo): string => {
  if (stepInfo.message) return stepInfo.message;
  return PROGRESS_STEP_MESSAGES[stepInfo.step as ProgressStepType] ?? stepInfo.step;
};

// Reducers are defined in a named const (rather than inline in createSlice) so
// the slice can carry an explicit `Slice<…, typeof reducers, …>` annotation
// below. Without it, the inferred slice type leaks immer's `WritableDraft` via a
// non-portable `.pnpm/immer` path and breaks .d.ts emit (TS2742). `state` is
// typed as the plain state (not a Draft) to keep the emitted types immer-free.
const reducers = {
  setResearchAssistantModel: (
    state: ResearchAssistantState,
    action: PayloadAction<ModelVersion>
  ) => {
    state.model = action.payload;
    state.messages = [];
  },
  addResearchAssistantMessages: (
    state: ResearchAssistantState,
    action: PayloadAction<MessageLegacy[]>
  ) => {
    const newMessages = action.payload;
    const lastMessage = state.messages[state.messages.length - 1];

    // Merge info messages that are adjacent to each other
    if (lastMessage) {
      const firstNewMessage = newMessages[0];
      if (firstNewMessage && lastMessage.type === firstNewMessage.type) {
        if (
          lastMessage.type &&
          [
            ResearchAssistantMessageEnumLegacy.INFO,
            ResearchAssistantMessageEnumLegacy.SAVE,
            ResearchAssistantMessageEnumLegacy.PROCESS,
            ResearchAssistantMessageEnumLegacy.REVERT,
          ].includes(lastMessage.type)
        ) {
          lastMessage.timestamp = firstNewMessage.timestamp;
          newMessages.shift();
        }
      }
    }

    // Merge adjacent info messages from the payload
    for (let i = 0; i < newMessages.length - 1; i++) {
      if (
        newMessages[i].type === ResearchAssistantMessageEnumLegacy.INFO &&
        newMessages[i + 1].type === ResearchAssistantMessageEnumLegacy.INFO
      ) {
        newMessages[i].timestamp = newMessages[i + 1].timestamp;
        newMessages.splice(i + 1, 1);
        i--;
      }
    }

    state.messages.push(...newMessages);
  },
  resetResearchAssistantMessages: (state: ResearchAssistantState) => {
    state.messages = [];
  },
  setResearchAssistantIsProcessing: (
    state: ResearchAssistantState,
    action: PayloadAction<{ transactionId: string }>
  ) => {
    state.isProcessing = true;
    state.transactionId = action.payload.transactionId;
    // Progress message is created by setProgressStep when the first backend
    // progress event arrives — not here — to avoid ordering issues with the
    // user message which may be added after this action.
  },
  setResearchAssistantIsReady: (state: ResearchAssistantState, action: PayloadAction<boolean>) => {
    state.isReady = action.payload;
  },
  clearResearchAssistantIsProcessing: (
    state: ResearchAssistantState,
    action: PayloadAction<{ transactionId?: string }>
  ) => {
    if (!action.payload.transactionId || state.transactionId === action.payload.transactionId) {
      state.isProcessing = false;
      state.transactionId = null;
      state.progress = { currentStep: null, avProgress: null, workflowId: null };

      // If the progress message has AV items, mark it complete so it stays
      // visible in history.  Otherwise remove it — requests that didn't
      // involve cohort definition generation shouldn't leave a progress block.
      const progressIdx = state.messages.findIndex(
        (m) =>
          m.type === ResearchAssistantMessageEnumLegacy.PROGRESS &&
          m.progressData &&
          !m.progressData.isComplete
      );
      if (progressIdx >= 0) {
        const progressMsg = state.messages[progressIdx];
        if (progressMsg.progressData && progressMsg.progressData.avProgress.length > 0) {
          progressMsg.progressData.isComplete = true;
        } else {
          state.messages.splice(progressIdx, 1);
        }
      }
    }
  },
  resetResearchAssistantState: () => initialState,
  setWorkflowId: (state: ResearchAssistantState, action: PayloadAction<string>) => {
    state.progress.workflowId = action.payload;
  },
  setProgressStep: (state: ResearchAssistantState, action: PayloadAction<ProgressStepInfo>) => {
    state.progress = { ...state.progress, currentStep: action.payload };

    // Upsert a progress message in the messages array
    const existing = findActiveProgressMessage(state.messages);
    const displayMessage = resolveProgressMessage(action.payload);

    if (existing && existing.progressData) {
      // Update existing progress message
      existing.progressData.step = action.payload.step;
      existing.progressData.message = displayMessage;
    } else {
      // Append new progress message
      state.messages.push({
        message: '',
        currentUser: 'research_assistant',
        isCurrentUser: false,
        type: ResearchAssistantMessageEnumLegacy.PROGRESS,
        progressData: {
          step: action.payload.step,
          message: displayMessage,
          avProgress: [],
          isComplete: false,
        },
      });
    }
  },
  updateAVProgress: (state: ResearchAssistantState, action: PayloadAction<AVProgressItem>) => {
    const item = action.payload;

    // Update ephemeral progress state
    if (!state.progress.avProgress) {
      state.progress.avProgress = [item];
    } else {
      const existing = state.progress.avProgress.findIndex((av) => av.avIndex === item.avIndex);
      if (existing >= 0) {
        state.progress.avProgress[existing] = item;
      } else {
        state.progress.avProgress.push(item);
      }
    }

    // Also update the progress message in the messages array
    const progressMsg = findActiveProgressMessage(state.messages);
    if (progressMsg?.progressData) {
      const avProgress = progressMsg.progressData.avProgress;
      const existingIdx = avProgress.findIndex((av) => av.avIndex === item.avIndex);
      if (existingIdx >= 0) {
        avProgress[existingIdx] = item;
      } else {
        avProgress.push(item);
      }
    }
  },
  completeProgressSteps: (state: ResearchAssistantState) => {
    // If the progress message has AV items, mark it complete so it stays
    // visible in history.  Otherwise remove it — requests that didn't
    // involve cohort definition generation shouldn't leave a progress block.
    const progressIdx = state.messages.findIndex(
      (m) =>
        m.type === ResearchAssistantMessageEnumLegacy.PROGRESS &&
        m.progressData &&
        !m.progressData.isComplete
    );
    if (progressIdx >= 0) {
      const progressMsg = state.messages[progressIdx];
      if (progressMsg.progressData && progressMsg.progressData.avProgress.length > 0) {
        progressMsg.progressData.isComplete = true;
      } else {
        state.messages.splice(progressIdx, 1);
      }
    }

    // Clear ephemeral progress state
    state.progress = { currentStep: null, avProgress: null, workflowId: null };
  },
  /**
   * Transition progress to a "waiting for response" state when the workflow
   * completes but the final RA message hasn't arrived yet.
   *
   * - AV items present: marks the stepper complete (stays visible in history)
   * - No AV items: shows "Preparing response" so the user still sees feedback
   *
   * Final cleanup happens in clearResearchAssistantIsProcessing when the
   * 'message' socket event arrives.
   */
  markWorkflowCompletedProgress: (state: ResearchAssistantState) => {
    const progressMsg = findActiveProgressMessage(state.messages);
    if (progressMsg?.progressData) {
      if (progressMsg.progressData.avProgress.length > 0) {
        progressMsg.progressData.isComplete = true;
      } else {
        progressMsg.progressData.message = 'Preparing response';
      }
    }
    // Clear ephemeral progress state but keep the message in the array
    state.progress = { currentStep: null, avProgress: null, workflowId: null };
  },
};

const researchAssistantSlice: Slice<ResearchAssistantState, typeof reducers, 'researchAssistant'> =
  createSlice({
    name: 'researchAssistant',
    initialState,
    reducers,
  });

export const {
  setResearchAssistantModel,
  addResearchAssistantMessages,
  resetResearchAssistantMessages,
  setResearchAssistantIsProcessing,
  resetResearchAssistantState,
  clearResearchAssistantIsProcessing,
  setResearchAssistantIsReady,
  setWorkflowId,
  setProgressStep,
  updateAVProgress,
  completeProgressSteps,
  markWorkflowCompletedProgress,
} = researchAssistantSlice.actions;

export { researchAssistantSlice };

declare module '@verantos-dev/app-state-base' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof researchAssistantSlice> {}
}

// Inject the slice into the root reducer
researchAssistantSlice.injectInto(rootReducer);
